import { DataColumn, ExportFormat, ExportOptions, GenericWarning, KpiWithChangeColumn } from '../../types';
import { ChartData } from '../data/common';
import { AvailableTemplate, ExcelExportDto, DataTable, ColumnType, DataCell } from '@ecosystem/export-api';
import { formatLabel, getExportContext, mapValueToDataCell } from '../../export';
import { Formatting } from '../../data/formatting';
import { getGlobalWarningMessages } from '../utils';

// This will hide from the first index the changes between two data labels
// This is a useful feature to have because the period that the user is focusing on usually has no changes
const HIDE_DATA_LABEL_CHANGE = 1;
const getEmptyValuesArray = (data: ChartData): (number | null)[] => data.dataLabels.map(() => null);

const createWorksheetData = (chartData: ChartData[], xColumn: DataColumn, currency: string): DataTable => {
  const dataColumns = chartData.flatMap(data => {
    const yColumn = data.dataLabels.map(label => ({
      header: `${data.yColumn.label}\n${formatLabel(label)}`,
      type: ColumnType.Text,
    }));

    const getPreviousPeriodLabel = (index, arr) => {
      return index === 0 ? arr[0] : arr[index - 1];
    };

    //Can convert data.yColumn to KpiWithChange, but doesn't have changeLabel as a type, only changeName
    const yColumnWithChange = data.dataLabels
      .map((label, idx, arr) => ({
        header: `${data.yColumn.label} change\n${formatLabel(getPreviousPeriodLabel(idx, arr))} vs ${formatLabel(
          label
        )}`,
        type: ColumnType.Text,
      }))
      .splice(HIDE_DATA_LABEL_CHANGE);

    return [...yColumn, ...yColumnWithChange];
  });

  const totalDataRows = [
    { type: ColumnType.Text, value: 'Market' },
    ...chartData.flatMap(data => {
      const valuesSet = data.totalsDataSet?.values.raw.length
        ? data.totalsDataSet.values.raw
        : getEmptyValuesArray(data);
      const valuesDataSet = data.totalsDataSet
        ? valuesSet.map(value => mapValueToDataCell(value, data.yColumn.formatHint, data.yColumn.dataType))
        : [];

      const changesSet = data.totalsDataSet?.changes?.raw.length
        ? data.totalsDataSet?.changes?.raw
        : getEmptyValuesArray(data);
      const changesDataSet = data.totalsDataSet
        ? changesSet
            .map(change =>
              mapValueToDataCell(change, (data.yColumn as KpiWithChangeColumn).changeFormatHint, data.yColumn.dataType)
            )
            .splice(HIDE_DATA_LABEL_CHANGE)
        : [];

      return [...valuesDataSet, ...changesDataSet];
    }),
  ];

  const columnLabels = chartData[0].dataSets.map(set => ({ type: ColumnType.Text, value: set.label }));

  //Explicity set value: number | string for column label
  const rowsPerKpi: DataCell[][][] = chartData.map(data =>
    data.dataSets.map(set => {
      const valuesSet = set.values?.raw.length ? set.values.raw : getEmptyValuesArray(data);
      const values = valuesSet.map(value => mapValueToDataCell(value, data.yColumn.formatHint, data.yColumn.dataType));

      const changesSet = set.changes?.raw.length ? set.changes.raw : getEmptyValuesArray(data);
      const changes = changesSet
        .map(change =>
          mapValueToDataCell(change, (data.yColumn as KpiWithChangeColumn).changeFormatHint, data.yColumn.dataType)
        )
        .splice(HIDE_DATA_LABEL_CHANGE);

      return [...values, ...changes];
    })
  );

  const multiKpi = (rowsPerKpi: DataCell[][][]) => {
    return columnLabels.map((label, index) => {
      return [label, ...rowsPerKpi.flatMap(row => row[index])];
    });
  };

  const singleKpi = (rowsPerKpi: DataCell[][]) => rowsPerKpi.map((row, index) => [columnLabels[index], ...row]);

  //Combines rows from an KPI into one row
  const dataRows = rowsPerKpi.length === 1 ? singleKpi(rowsPerKpi[0]) : multiKpi(rowsPerKpi);

  return {
    currencySymbol: Formatting.getCurrencySymbol(currency) || currency,
    dataColumns: [{ type: ColumnType.Text, header: xColumn.label }, ...dataColumns],
    dataRows: dataRows,
    columnWidths: [
      { columnNumber: 1, width: 20 },
      ...dataColumns.map((_, index: number) => ({ columnNumber: index + 2, width: 20 })),
    ],
    summaryRows: [totalDataRows],
  };
};

export const chartDataToExportData = (
  exportFormat: ExportFormat,
  { fileHeaderSettings, fileName }: ExportOptions,
  chartData: ChartData[],
  xColumn: DataColumn,
  currency: string,
  globalWarnings: GenericWarning[] = []
): ExcelExportDto => {
  switch (exportFormat) {
    case ExportFormat.Xlsx:
      return {
        template: AvailableTemplate.Excel,
        name: fileName,
        worksheets: [
          {
            context: [...getExportContext(fileHeaderSettings), ...getGlobalWarningMessages(globalWarnings)],
            worksheetName: 'scorecard',
            data: createWorksheetData(chartData, xColumn, currency),
          },
        ],
      };

    default:
  }
};
